import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "../contexts/ThemeContext";
import UsageIndicator from "./UsageIndicator";
//import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HistoryIcon from "@mui/icons-material/History";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptIcon from "@mui/icons-material/Receipt";
//import Brightness4Icon from "@mui/icons-material/Brightness4";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HomeIcon from "@mui/icons-material/Home";
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';

interface HeaderProps {
  userEmail: string;
  usageLabel: string | null;
  subscriptionLoading: boolean;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  userEmail, 
  usageLabel, 
  subscriptionLoading, 
  onLogout 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  //const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);

  const userName = userEmail?.split('@')[0] || 'User';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };


  // Redirect to external homepage
  const goToHomepage = () => {
    window.location.href = "https://extractfast.com";
  };

  const isActive = (path: string) => location.pathname === path;

  const getTextColor = () => {
    return mode === 'light' ? '#0A1E3C' : '#E5E5E5';
  };

  const getActiveTabColor = () => {
    return mode === 'light' ? '#FDBB30' : '#FFD966';
  };

  const getButtonHoverBg = () => {
    return mode === 'light' 
      ? 'rgba(253, 187, 48, 0.08)' 
      : 'rgba(255, 217, 102, 0.12)';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: mode === 'light' 
          ? 'rgba(255, 255, 255, 0.95)' 
          : 'rgba(18, 18, 18, 0.95)',
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${mode === 'light' 
          ? 'rgba(10, 30, 60, 0.08)' 
          : 'rgba(255, 255, 255, 0.08)'}`,
        boxShadow: mode === 'light'
          ? "0 4px 20px rgba(0, 0, 0, 0.02)"
          : "0 4px 20px rgba(0, 0, 0, 0.4)",
        color: getTextColor()
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        {/* Logo Section - redirects to homepage */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer"
          }}
          onClick={goToHomepage}
        >
          <Box
            component="img"
            src="/ppp.png"
            alt="PPP Logo"
            sx={{
              width: 48,
              height: 48,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BrandLogo variant="h6" />
          </Box>
        </Box>

        {/* Main Navigation Tabs - Added HOME tab */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={goToHomepage}
            sx={{
              color: '#0A2540',
              fontWeight: 500,
              borderRadius: 0,
              px: 2,
              '&:hover': {
                color: '#2A9D8F',
                backgroundColor: 'rgba(42, 157, 143, 0.04)'
              }
            }}
          >
            Home
          </Button>

          <Button
            color="inherit"
            startIcon={<CloudUploadIcon />}
            onClick={() => navigate("/")}
            sx={{
              color: isActive('/') ? '#2A9D8F' : '#0A2540',
              fontWeight: isActive('/') ? 700 : 500,
              borderBottom: isActive('/') ? `2px solid #2A9D8F` : 'none',
              borderRadius: 0,
              px: 2,
              '&:hover': {
                color: '#2A9D8F',
                backgroundColor: 'rgba(42, 157, 143, 0.04)'
              }
            }}
          >
            Upload
          </Button>

          <Button
            color="inherit"
            startIcon={<HistoryIcon />}
            onClick={() => navigate("/history")}
            sx={{
              color: isActive('/history') ? '#2A9D8F' : '#0A2540',
              fontWeight: isActive('/history') ? 700 : 500,
              borderBottom: isActive('/history') ? `2px solid #2A9D8F` : 'none',
              borderRadius: 0,
              px: 2,
              '&:hover': {
                color: '#2A9D8F',
                backgroundColor: 'rgba(42, 157, 143, 0.04)'
              }
            }}
          >
            History
          </Button>
          
          <Button
            color="inherit"
            startIcon={<CreditCardIcon />}
            onClick={() => navigate("/subscription")}
            sx={{
              color: isActive('/subscription') ? '#2A9D8F' : '#0A2540',
              fontWeight: isActive('/subscription') ? 700 : 500,
              borderBottom: isActive('/subscription') ? `2px solid #2A9D8F` : 'none',
              borderRadius: 0,
              px: 2,
              '&:hover': {
                color: '#2A9D8F',
                backgroundColor: 'rgba(42, 157, 143, 0.04)'
              }
            }}
          >
            Subscription
          </Button>
          
          <Button
            color="inherit"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate("/purchase-history")}
            sx={{
              color: isActive('/purchase-history') ? '#2A9D8F' : '#0A2540',
              fontWeight: isActive('/purchase-history') ? 700 : 500,
              borderBottom: isActive('/purchase-history') ? `2px solid #2A9D8F` : 'none',
              borderRadius: 0,
              px: 2,
              '&:hover': {
                color: '#2A9D8F',
                backgroundColor: 'rgba(42, 157, 143, 0.04)'
              }
            }}
          >
            Purchases
          </Button>
        </Box>

        {/* Right section - same as before */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ minWidth: 200 }}>
            {subscriptionLoading ? (
              <Typography variant="caption" sx={{ color: getTextColor() }}>
                Loading usage...
              </Typography>
            ) : usageLabel ? (
              <UsageIndicator />
            ) : null}
          </Box>


          <NotificationBell />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              onClick={handleMenuOpen}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#FDBB30",
                color: "#0A1E3C",
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '1rem',
                boxShadow: mode === 'light'
                  ? '0 2px 8px rgba(253, 187, 48, 0.3)'
                  : '0 2px 8px rgba(255, 217, 102, 0.2)'
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          </Box>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                bgcolor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                border: `1px solid ${mode === 'light' 
                  ? 'rgba(10, 30, 60, 0.08)' 
                  : 'rgba(255, 255, 255, 0.08)'}`,
              }
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: getTextColor() }}>
                {userEmail}
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                <span style={{ color: '#214F80' }}>Extract</span>
                <span style={{ color: '#C6602F' }}>Fast</span>
                <span style={{ color: '#11579c' }}>.com</span>
              </Typography>
            </Box>
            <Divider sx={{ 
              my: 1,
              borderColor: mode === 'light' 
                ? 'rgba(10, 30, 60, 0.08)' 
                : 'rgba(255, 255, 255, 0.08)'
            }} />
            <MenuItem 
              onClick={() => { handleMenuClose(); navigate("/settings"); }}
              sx={{
                color: getTextColor(),
                '&:hover': {
                  bgcolor: getButtonHoverBg()
                }
              }}
            >
              <SettingsIcon sx={{ mr: 1.5, fontSize: 20 }} />
              Settings
            </MenuItem>
            <MenuItem 
              onClick={onLogout} 
              sx={{
                color: '#d32f2f',
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.08)'
                }
              }}
            >
              <ExitToAppIcon sx={{ mr: 1.5, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Mobile Navigation */}
      <Box sx={{ 
        display: { xs: 'flex', md: 'none' }, 
        justifyContent: 'space-around', 
        p: 1, 
        borderTop: `1px solid ${mode === 'light' 
          ? 'rgba(10, 30, 60, 0.08)' 
          : 'rgba(255, 255, 255, 0.08)'}`,
        bgcolor: mode === 'light' 
          ? 'rgba(255, 255, 255, 0.5)' 
          : 'rgba(18, 18, 18, 0.5)'
      }}>
        <IconButton 
          onClick={goToHomepage} 
          size="small"
          sx={{ color: getTextColor() }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton 
          color={isActive('/') ? 'secondary' : 'default'} 
          onClick={() => navigate("/")} 
          size="small"
          sx={{
            color: isActive('/') ? getActiveTabColor() : getTextColor(),
          }}
        >
          <CloudUploadIcon />
        </IconButton>
        <IconButton 
          color={isActive('/history') ? 'secondary' : 'default'} 
          onClick={() => navigate("/history")} 
          size="small"
          sx={{
            color: isActive('/history') ? getActiveTabColor() : getTextColor(),
          }}
        >
          <HistoryIcon />
        </IconButton>
        <IconButton 
          color={isActive('/subscription') ? 'secondary' : 'default'} 
          onClick={() => navigate("/subscription")} 
          size="small"
          sx={{
            color: isActive('/subscription') ? getActiveTabColor() : getTextColor(),
          }}
        >
          <CreditCardIcon />
        </IconButton>
        <IconButton 
          color={isActive('/purchase-history') ? 'secondary' : 'default'} 
          onClick={() => navigate("/purchase-history")} 
          size="small"
          sx={{
            color: isActive('/purchase-history') ? getActiveTabColor() : getTextColor(),
          }}
        >
          <ReceiptIcon />
        </IconButton>
      </Box>
    </AppBar>
  );
};

export default Header;